/**
 * Excel Cash Flow Model Service - Browser Version
 * ===============================================
 * 
 * Browser-compatible version of excelCFModelService.js
 * Transforms cash JSON model into Excel workbook for download
 */

import ExcelJS from 'exceljs';
import JSZip from 'jszip';
import { CHART_TEMPLATE_BASE64 } from './chartTemplate';
import { styleWorkbook } from './excelDesignerBrowser';

/** Track synthetic total rows created by addGroupTotal */
const SYN_TOTAL_ROWS = new Set();

/** Track "Drivers" sub-header rows for styling */
const DRIVER_HEADER_ROWS = new Set();

/** Track display-group header rows (e.g. "Payroll") for styling */
const DISPLAY_GROUP_HEADER_ROWS = new Set();

/** Track display-group subtotal rows for styling */
const DISPLAY_GROUP_SUBTOTAL_ROWS = new Set();

const WORKSHEET_TAB_BLUE = 'FF2F5496';

/** Convert #RRGGBB or RRGGBB hex to FFRRGGBB ARGB for ExcelJS */
function hexToArgb(hex) {
  if (!hex) return null;
  const clean = hex.replace('#', '').toUpperCase();
  if (clean.length === 6) return `FF${clean}`;
  if (clean.length === 8) return clean;
  return null;
}

/**
 * Resolve branding colors from org.clientPortal.
 * Returns ARGB strings for primary / secondary / accent, falling back to
 * the default navy palette when a brand color isn't set.
 */
function buildBrandingColors(branding = {}) {
  const primary   = hexToArgb(branding.primaryColor)   || 'FF1F4E79';
  const secondary = hexToArgb(branding.secondaryColor) || 'FF2F5496';
  const accent    = hexToArgb(branding.accentColor)    || 'FF4A90E2';
  return { primary, secondary, accent };
}

/**
 * Blend an ARGB color toward white at the given alpha (0–1).
 * alpha=0.15 means 15% color, 85% white — a very light tint.
 */
function lightTint(argb, alpha = 0.15) {
  if (!argb || argb.length < 8) return 'FFFFFFFF';
  const r = parseInt(argb.slice(2, 4), 16);
  const g = parseInt(argb.slice(4, 6), 16);
  const b = parseInt(argb.slice(6, 8), 16);
  const blend = (c) => Math.round(255 * (1 - alpha) + c * alpha);
  const toHex = (v) => blend(v).toString(16).padStart(2, '0').toUpperCase();
  return `FF${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Return true when an ARGB colour is perceptually "light" (needs dark text).
 * Uses the W3C relative-luminance formula with a 0.45 threshold.
 */
function isLightColor(argb) {
  if (!argb || argb.length < 8) return false;
  const r = parseInt(argb.slice(2, 4), 16) / 255;
  const g = parseInt(argb.slice(4, 6), 16) / 255;
  const b = parseInt(argb.slice(6, 8), 16) / 255;
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum > 0.45;
}

/** Helper function to get label text */
const getLabel = (node) => {
  if (typeof node.label === 'string') return node.label;
  return node.label?.text ?? node.id ?? '(unnamed)';
};

/** Extract periods from calendar data */
function extractPeriods(data) {
  return [
    ...(data.calendar?.periods?.actual ?? []),
    ...(data.calendar?.periods?.forecast ?? [])
  ];
}

/** Create workbook and worksheet */
function createWorkbook(branding = {}) {
  const { primary } = buildBrandingColors(branding);
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Model', {
    properties: { tabColor: { argb: primary } }
  });
  ws.views = [{ showGridLines: false, state: 'frozen', xSplit: 2, ySplit: 0 }];
  ws.pageSetup = {
    orientation: 'landscape',
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    printTitlesRow: '1:6',
    margins: { left: 0.3, right: 0.3, top: 0.3, bottom: 0.3, header: 0, footer: 0 },
  };
  return { wb, ws };
}

/** Format a date string for subtitle display (e.g. "Jan 7, 2024") */
function formatDatePretty(dateStr) {
  if (!dateStr) return '';
  const dt = new Date(dateStr + 'T00:00:00Z');
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function getForecastSubtitleInfo(calendar = {}) {
  const actualPeriods = calendar.periods?.actual || [];
  const forecastPeriods = calendar.periods?.forecast || [];
  const granularity = calendar.granularity || 'week';
  const granLabel = granularity.charAt(0).toUpperCase() + granularity.slice(1);
  const subtitlePeriods = forecastPeriods.length > 0 ? forecastPeriods : [...actualPeriods, ...forecastPeriods];
  const startDatePretty = subtitlePeriods[0] ? formatDatePretty(subtitlePeriods[0]) : '';
  const endDatePretty = subtitlePeriods[subtitlePeriods.length - 1]
    ? formatDatePretty(subtitlePeriods[subtitlePeriods.length - 1])
    : '';
  const subtitleBase = `${subtitlePeriods.length}-${granLabel} Cash Flow Model`;
  const subtitle = startDatePretty && endDatePretty
    ? `${subtitleBase}  |  ${startDatePretty} – ${endDatePretty}`
    : subtitleBase;

  return {
    actualPeriods,
    forecastPeriods,
    granularity,
    granLabel,
    subtitlePeriods,
    subtitle,
  };
}

/** Add title, subtitle, and header rows with dates */
function addHeaderRow(ws, periods, actualPeriodCount, options = {}) {
  const title = options.title || 'Detailed Model';
  const subtitleInfo = getForecastSubtitleInfo(options.calendar || {});

  // Row 1: Title
  const titleRow = ws.addRow(['', title]);
  titleRow.getCell(2).font = { name: 'Calibri', size: 16, bold: true };
  titleRow.height = 26;
  titleRow.getCell(2).alignment = { horizontal: 'left', vertical: 'middle' };

  // Row 2: Subtitle — period description and date range
  const subtitleRow = ws.addRow(['', subtitleInfo.subtitle]);
  subtitleRow.getCell(2).font = { name: 'Calibri', size: 10, italic: true };
  subtitleRow.height = 18;
  subtitleRow.getCell(2).alignment = { horizontal: 'left', vertical: 'middle' };

  // Row 3: Blank spacer
  ws.addRow([]);

  // Row 4: Actual / Forecast column labels
  const periodTypeRow = ws.addRow(['', '']);
  for (let i = 0; i < periods.length; i++) {
    const cell = periodTypeRow.getCell(i + 3); // Start at column C
    if (i < actualPeriodCount) {
      cell.value = 'Actual';
    } else {
      cell.value = 'Forecast';
    }
    cell.alignment = { horizontal: 'center' };
  }

  // Row 5: Dates starting at column C — stored as Date objects so Excel
  // can apply numFmt for display while the ISO string lookup still works
  // via buildDateCol (which converts Date objects back to ISO strings).
  const dateRow = ws.addRow(['', '']);
  for (let i = 0; i < periods.length; i++) {
    const cell = dateRow.getCell(i + 3);
    // Store as Date object so numFmt renders correctly in Excel
    cell.value  = new Date(periods[i] + 'T00:00:00Z');
    cell.numFmt = 'mmm d';
    cell.alignment = { horizontal: 'center' };
  }
}

/** Add blank spacer row */
function addSpacerRow(ws) {
  ws.addRow([]);
}

/** Add table banner row */
function addTableBanner(ws, table) {
  const bannerText = String(table.header || table.id || 'TABLE').toUpperCase();
  ws.addRow(['', bannerText]);
}

// ─────────────────────────────────────────────────────────────
// Layout helpers - place rows/groups onto the worksheet
// ─────────────────────────────────────────────────────────────

/**
 * Add driver rows from a virtual driver group into the sheet.
 * Adds a "Drivers" sub-header first, then the driver children.
 *
 * @param {string} sectionId - Parent section id (e.g. "op_inflows")
 * @param {string} groupId   - Parent group id   (e.g. "main")
 */
function addDriverGroupRows(ws, driversGroup, rowById, sectionId, groupId) {
  if (!driversGroup.children || driversGroup.children.length === 0) return;

  // Add "Drivers" sub-header row (no values, just a label)
  const headerRow = ws.addRow(['', '  Drivers']);
  DRIVER_HEADER_ROWS.add(headerRow.number);

  for (const driver of driversGroup.children || []) {
    const label = '    ' + getLabel(driver); // extra indent under Drivers header
    const addedRow = ws.addRow(['', label]);
    
    // Full-path key: section.group.row.driver — guarantees uniqueness across sections
    const fullPath = `${sectionId}.${groupId}.${driver._parentRowId}.${driver.id}`;
    rowById[fullPath] = addedRow.number;
    
    // Also store by parentRowId.driverId (shorter, used by some formula references)
    const driverKey = `${driver._parentRowId}.${driver.id}`;
    rowById[driverKey] = addedRow.number;
    
    // NOTE: do NOT store by bare driver.id — it causes collisions when
    // multiple rows share the same driver name (e.g. "trailing_avg").
  }
}

/** Add child rows for a group (non-summary sections).
 *  NOTE: Virtual driver groups are SKIPPED here — they are added after the
 *  total row by processGroup so that the layout is:
 *     child rows → Total → Drivers sub-header + driver rows
 */
function addGroupChildRows(ws, group, rowById, sectionId) {
  // Sort row children by display_order; items without it (or non-rows) stay in
  // their original relative order at the end (stable sort, Infinity sentinel).
  const children = [...(group.children || [])].sort((a, b) => {
    const aOrd = a.kind === 'row' && a.display_order != null ? a.display_order : Infinity;
    const bOrd = b.kind === 'row' && b.display_order != null ? b.display_order : Infinity;
    return aOrd - bOrd;
  });
  for (const child of children) {
    // Skip virtual driver groups — added after total by processGroup
    if (child.kind === 'group' && child._isVirtualDriverGroup) {
      continue;
    }
    
    // Skip ref children in non-summary sections
    if (child._isRefChild) continue;
    
    if (child.kind === 'row') {
      const addedRow = ws.addRow(['', getLabel(child)]);
      if (child.id) rowById[child.id] = addedRow.number;
    } else if (child.kind === 'group') {
      // Nested sub-group (rare)
      const groupRow = ws.addRow(['', getLabel(child)]);
      if (child.id) rowById[child.id] = groupRow.number;
      addGroupChildRows(ws, child, rowById, sectionId);
    }
  }
}

/**
 * Check if a group's rows use display-group labels (`group` field).
 * Returns a Map of groupLabel -> [rows] if any exist, or null.
 */
function detectDisplayGroups(group) {
  const rows = (group.children || []).filter(
    c => c.kind === 'row' && !c._isDriverDisplay && !c._isRefChild
  );
  if (!rows.some(r => r.group)) return null;

  const buckets = new Map(); // label -> [rows]
  const ungrouped = [];
  for (const row of rows) {
    if (row.group) {
      if (!buckets.has(row.group)) buckets.set(row.group, []);
      buckets.get(row.group).push(row);
    } else {
      ungrouped.push(row);
    }
  }
  return { buckets, ungrouped };
}

/**
 * Lay out a single display group:
 *   Header row  (e.g. "Payroll")
 *   Indented child rows
 *   Subtotal row
 *   Drivers section (if any)
 *
 * Returns the subtotal row number so the main-group total can SUM them.
 */
function addDisplayGroup(ws, label, rows, allDriverItems, rowById, sectionId, groupId) {
  // Header row
  const headerRow = ws.addRow(['', label]);
  DISPLAY_GROUP_HEADER_ROWS.add(headerRow.number);

  // Child rows (indented)
  const childRowNums = [];
  for (const row of rows) {
    const addedRow = ws.addRow(['', '  ' + getLabel(row)]);
    if (row.id) rowById[row.id] = addedRow.number;
    childRowNums.push(addedRow.number);
  }

  // Subtotal row — store child row numbers for SUM formula later
  const subtotalRow = ws.addRow(['', `  Subtotal ${label}`]);
  DISPLAY_GROUP_SUBTOTAL_ROWS.add(subtotalRow.number);
  const subtotalId = `_dg_subtotal_${label.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`;
  rowById[subtotalId] = subtotalRow.number;
  if (!ws._displayGroupSubtotalMap) ws._displayGroupSubtotalMap = {};
  ws._displayGroupSubtotalMap[subtotalRow.number] = childRowNums;

  // Drivers for these rows (filtered from the master list)
  const rowIdSet = new Set(rows.map(r => r.id));
  const matchingDrivers = allDriverItems.filter(d => rowIdSet.has(d._parentRowId));
  if (matchingDrivers.length > 0) {
    addSpacerRow(ws); // Visual gap between subtotal and Drivers header
    const driversHeaderRow = ws.addRow(['', '    Drivers']);
    DRIVER_HEADER_ROWS.add(driversHeaderRow.number);
    for (const driver of matchingDrivers) {
      const driverLabel = '      ' + getLabel(driver);
      const addedRow = ws.addRow(['', driverLabel]);
      const fullPath = `${sectionId}.${groupId}.${driver._parentRowId}.${driver.id}`;
      rowById[fullPath] = addedRow.number;
      const driverKey = `${driver._parentRowId}.${driver.id}`;
      rowById[driverKey] = addedRow.number;
    }
  }

  return subtotalRow.number;
}

/** Add total line for a group
 *  @param {string} fullPath - Full path to the group (e.g. "summary.financing")
 */
function addGroupTotal(ws, group, rowById, fullPath, labelOverride) {
  const groupLabel = labelOverride || getLabel(group);
  const row = ws.addRow(['', `Total ${groupLabel}`]);
  SYN_TOTAL_ROWS.add(row.number);
  
  // Store by full path so resolvePathToRow can find summary vs data section totals
  if (fullPath) {
    rowById[fullPath] = row.number;
  }
  
  return row.number;
}

/** Check if a group should have a total row */
function shouldHaveTotal(group) {
  if (group._isVirtualDriverGroup) return false;
  if (group.forecast === null) return false; // explicitly null → no forecast total
  return (group.actuals || group.forecast || group.cache);
}

/** Process a single group */
function processGroup(ws, group, rowById, sectionId) {
  if (group.kind === 'group') {
    // Check for display-group organisation (rows with `group` field)
    const displayInfo = detectDisplayGroups(group);

    if (displayInfo) {
      // ── Display-group layout ─────────────────────────────
      const driversGroup = (group.children || []).find(c => c._isVirtualDriverGroup);
      const allDriverItems = driversGroup?.children || [];

      // Sort buckets by the display_order of their first row (if present),
      // otherwise by absolute value sum (largest first) matching frontend behavior
      const sortedBuckets = [...displayInfo.buckets.entries()].sort((a, b) => {
        const aFirst = a[1][0];
        const bFirst = b[1][0];
        const aOrd = aFirst?.display_order;
        const bOrd = bFirst?.display_order;
        if (aOrd != null && bOrd != null) return aOrd - bOrd;
        if (aOrd != null) return -1;
        if (bOrd != null) return 1;
        return 0;
      });

      // Lay out each display group
      const subtotalRowNums = [];
      for (const [label, rows] of sortedBuckets) {
        const subtotalNum = addDisplayGroup(ws, label, rows, allDriverItems, rowById, sectionId, group.id);
        subtotalRowNums.push(subtotalNum);
        addSpacerRow(ws);
      }

      // Ungrouped rows (+ their drivers)
      const ungroupedRowNums = [];
      for (const row of displayInfo.ungrouped) {
        const addedRow = ws.addRow(['', getLabel(row)]);
        if (row.id) rowById[row.id] = addedRow.number;
        ungroupedRowNums.push(addedRow.number);
      }
      if (displayInfo.ungrouped.length > 0) {
        const ungroupedIds = new Set(displayInfo.ungrouped.map(r => r.id));
        const ungroupedDrivers = allDriverItems.filter(d => ungroupedIds.has(d._parentRowId));
        if (ungroupedDrivers.length > 0) {
          const dhRow = ws.addRow(['', '  Drivers']);
          DRIVER_HEADER_ROWS.add(dhRow.number);
          for (const driver of ungroupedDrivers) {
            const driverLabel = '    ' + getLabel(driver);
            const addedRow = ws.addRow(['', driverLabel]);
            const fullPath = `${sectionId}.${group.id}.${driver._parentRowId}.${driver.id}`;
            rowById[fullPath] = addedRow.number;
            rowById[`${driver._parentRowId}.${driver.id}`] = addedRow.number;
          }
        }
      }

      // Main-group total (SUMs display-group subtotals + ungrouped rows)
      if (shouldHaveTotal(group)) {
        const fullPath = `${sectionId}.${group.id}`;
        const totalRowNum = addGroupTotal(ws, group, rowById, fullPath);
        // Store mapping so populateSheet can build SUM(subtotals + ungrouped)
        if (!ws._displayGroupTotalMap) ws._displayGroupTotalMap = {};
        ws._displayGroupTotalMap[totalRowNum] = [...subtotalRowNums, ...ungroupedRowNums];
      }

    } else {
      // ── Standard flat layout ────────────────────────────
      addGroupChildRows(ws, group, rowById, sectionId);
      if (shouldHaveTotal(group)) {
        const fullPath = `${sectionId}.${group.id}`;
        addGroupTotal(ws, group, rowById, fullPath);
      }
      // Add driver groups AFTER the total row, with a spacer row in between
      const driverGroups = (group.children || []).filter(
        c => c.kind === 'group' && c._isVirtualDriverGroup && c.children?.length > 0
      );
      if (driverGroups.length > 0) {
        addSpacerRow(ws);
        for (const dg of driverGroups) {
          addDriverGroupRows(ws, dg, rowById, sectionId, group.id);
        }
      }
    }
  } else if (group.kind === 'row') {
    const row = ws.addRow(['', getLabel(group)]);
    if (group.id) rowById[group.id] = row.number;
  }
}

/**
 * Process the summary section specially:
 * - Groups get their childrenRef rows laid out as formula-reference rows
 * - Each summary group gets a Total row that SUMs those ref rows
 * - Direct rows (beginning_cash, change, ending_cash) are laid out normally
 */
function processSummaryTable(ws, table, rowById) {
  const startRow = ws.rowCount + 1;
  addTableBanner(ws, table); // dark-blue section title row

  for (const child of table.children || []) {
    if (child.kind === 'group') {
      // Lay out ref children (from childrenRef) as indented rows
      const refChildRowNums = [];

      for (const refChild of child.children || []) {
        // Skip virtual driver groups in summary
        if (refChild._isVirtualDriverGroup) continue;
        if (refChild._isDriverDisplay) continue;

        if (refChild._isRefChild || refChild.kind === 'row') {
          const refRow = ws.addRow(['', '  ' + getLabel(refChild)]);
          refChildRowNums.push(refRow.number);

          // Store with unique key: _ref_{groupId}_{childId}
          const refKey = `_ref_${child.id}_${refChild.id}`;
          rowById[refKey] = refRow.number;
        }
      }

      // Add Total row for this summary group
      const fullPath = `summary.${child.id}`;
      const totalRowNum = addGroupTotal(ws, child, rowById, fullPath);

      // Store the mapping: totalRowNum → list of ref child row numbers
      // so populateSheet can build SUM formulas
      if (!ws._summaryTotalMap) ws._summaryTotalMap = {};
      ws._summaryTotalMap[totalRowNum] = refChildRowNums;

    } else if (child.kind === 'row') {
      // Direct rows: beginning_cash, change, ending_cash
      const row = ws.addRow(['', getLabel(child)]);
      if (child.id) rowById[child.id] = row.number;
    }
  }

  const endRow = ws.rowCount;
  return { tableId: table.id, startRow, endRow };
}

/** Process a single table (section) */
function processTable(ws, table, rowById) {
  // Summary section gets special handling
  if (table.id === 'summary') {
    return processSummaryTable(ws, table, rowById);
  }

  const startRow = ws.rowCount + 1;
  addTableBanner(ws, table);
  
  for (const child of table.children || []) {
    processGroup(ws, child, rowById, table.id);
  }
  
  const endRow = ws.rowCount;
  return { tableId: table.id, startRow, endRow };
}

/** Process all tables in the model */
function processModel(ws, model) {
  const tableRanges = [];
  const rowById = {};
  
  for (let i = 0; i < model.length; i++) {
    const table = model[i];
    
    if (i > 0) {
      addSpacerRow(ws);
    }
    
    const range = processTable(ws, table, rowById);
    tableRanges.push(range);
  }
  
  return { tableRanges, rowById };
}

// ─────────────────────────────────────────────────────────────
// Address helpers
// ─────────────────────────────────────────────────────────────

/** Convert a 1-based column number to Excel column letter(s) (1=A, 27=AA, etc.) */
function colToLetter(col) {
  let result = '';
  let c = col;
  while (c > 0) {
    c--;
    result = String.fromCharCode(65 + (c % 26)) + result;
    c = Math.floor(c / 26);
  }
  return result;
}

/** Get Excel cell address string from row number and column number */
function cellAddr(row, col) {
  return `${colToLetter(col)}${row}`;
}

/** Build date -> column map using header row 5 (dates start at C5).
 *  Handles both ISO string values (legacy) and Date objects (new format).
 */
function buildDateCol(ws) {
  const HEADER_ROW = 5;
  const LABEL_COL  = 2;
  const map = {};
  const header = ws.getRow(HEADER_ROW);
  header.eachCell((cell, colNumber) => {
    if (colNumber <= LABEL_COL) return;
    const val = cell.value;
    if (!val) return;
    if (val instanceof Date) {
      // Convert Date object → ISO date string (YYYY-MM-DD) using UTC
      const iso = val.toISOString().slice(0, 10);
      map[iso] = colNumber;
    } else {
      // Legacy: raw ISO string stored in cell
      map[String(val)] = colNumber;
    }
  });
  return map;
}

/** Find label -> row map scanning col B */
function findLabelRows(ws) {
  const HEADER_ROW = 5;
  const LABEL_COL = 2;
  const map = {};
  ws.eachRow((row, rowNumber) => {
    if (rowNumber <= HEADER_ROW) return;
    const label = (row.getCell(LABEL_COL).value || '').toString().trim();
    if (label) map[label] = rowNumber;
  });
  return map;
}

function nodeLabel(node) {
  return typeof node.label === 'string' ? node.label : (node.id || '');
}

// ─────────────────────────────────────────────────────────────
// Index the model tree so we can look up any node by id / path
// ─────────────────────────────────────────────────────────────

function indexNodes(model) {
  const byLabel = {};
  const byId = {};
  const byPath = {};
  
  for (const table of model || []) {
    const walkSection = (section) => {
      if (!section) return;
      byId[section.id] = section;
      byPath[section.id] = section;
      
      for (const group of section.children || []) {
        if (group.kind !== 'group') {
          // Direct row on section
          if (group.kind === 'row') {
            const lbl = nodeLabel(group);
            if (lbl) byLabel[lbl] = group;
            byId[group.id] = group;
            byPath[`${section.id}.${group.id}`] = group;
          }
          continue;
        }
        
        // It's a group
        const lbl = nodeLabel(group);
        if (lbl) byLabel[lbl] = group;
        byId[group.id] = group;
        const groupPath = `${section.id}.${group.id}`;
        byPath[groupPath] = group;
        
        for (const child of group.children || []) {
          if (child._isRefChild) continue; // skip mirrored children
          
          // Virtual driver group
          if (child.kind === 'group' && child._isVirtualDriverGroup) {
            for (const driver of child.children || []) {
              const driverKey = `${driver._parentRowId}.${driver.id}`;
              const driverPath = `${section.id}.${group.id}.${driver._parentRowId}.${driver.id}`;
              // Store by full path AND shorter key in both maps
              byId[driverPath] = driver;
              byId[driverKey] = driver;
              byPath[driverPath] = driver;
              byPath[driverKey] = driver;
            }
            continue;
          }
          
          if (child.kind === 'row') {
            const childLbl = nodeLabel(child);
            if (childLbl) byLabel[childLbl] = child;
            byId[child.id] = child;
            byPath[`${section.id}.${group.id}.${child.id}`] = child;
            
            // Index real drivers (attached to row via .drivers array)
            if (child.drivers) {
              for (const d of child.drivers) {
                const dk = `${child.id}.${d.id}`;
                byId[dk] = d;
                byPath[`${section.id}.${group.id}.${child.id}.${d.id}`] = d;
              }
            }
          }
        }
      }
    };
    walkSection(table);
  }
  
  return { byLabel, byId, byPath };
}

/** Apply formatting for an entire row across all date columns */
function applyRowFormat(ws, rowNumber, type, dateColMap) {
  const cols = Object.values(dateColMap);
  if (cols.length === 0) return;
  const first = Math.min(...cols);
  const last = Math.max(...cols);
  for (let col = first; col <= last; col++) {
    const cell = ws.getCell(rowNumber, col);
    if (type === 'percent' || type === 'percentage') {
      cell.numFmt = '0.0%';
    } else if (type === 'currency') {
      cell.numFmt = '$#,##0_);[Red]($#,##0)';
    } else if (type === 'number') {
      cell.numFmt = '#,##0';
    }
  }
}

// ─────────────────────────────────────────────────────────────
// Formula translation:  backend expression → Excel formula
// ─────────────────────────────────────────────────────────────

/**
 * Convert a backend expression to an Excel formula.
 * Returns the formula string (without leading =) or null if not translatable.
 */
function convertExprToExcelFormula(expr, node, rowNumber, colNum, dateCol, modelIndex, rowById, labelRow, firstDateCol) {
  if (!expr || typeof expr !== 'string') return null;
  
  let trimmed = expr.trim();
  
  // Strip FORMULA() wrapper if present
  if (trimmed.startsWith('FORMULA(') && trimmed.endsWith(')')) {
    trimmed = trimmed.slice(8, -1).trim();
  }
  
  // ── SUM(CHILDREN) ────────────────────────────────────────
  if (trimmed === 'SUM(CHILDREN)') {
    const childRows = [];
    if (node.children) {
      for (const c of node.children) {
        if (c._isVirtualDriverGroup) continue;
        if (c._isDriverDisplay) continue;
        if (c._isRefChild) {
          const rn = rowById[c.id];
          if (rn) childRows.push(cellAddr(rn, colNum));
        } else if (c.kind === 'row') {
          const rn = rowById[c.id];
          if (rn) childRows.push(cellAddr(rn, colNum));
        }
      }
    }
    if (node.childrenRef && childRows.length === 0) {
      const [refSec, refGrp] = node.childrenRef.split('.');
      const totalLabel = `Total ${nodeLabel(modelIndex.byId[refGrp] || {})}`;
      const totalRow = labelRow[totalLabel];
      if (totalRow) return cellAddr(totalRow, colNum);
    }
    if (childRows.length) return `SUM(${childRows.join(',')})`;
    return null;
  }
  
  // ── self[-1]  (previous period) ──────────────────────────
  if (trimmed === 'self[-1]') {
    if (colNum <= firstDateCol) return null;
    return cellAddr(rowNumber, colNum - 1);
  }
  
  // ── self (current period, same row) ─────────────────────
  if (trimmed === 'self') {
    return cellAddr(rowNumber, colNum);
  }
  
  // ── AVERAGE/MIN/MAX/SUM(ref[-N:-M]) — unified handler for self and path refs ──
  const fnRangeMatch = trimmed.match(/^(AVERAGE|MIN|MAX|SUM)\(\s*([a-zA-Z_][a-zA-Z0-9_.]*)\s*\[\s*(-?\d+)\s*:\s*(-?\d+)\s*\]\s*\)$/i);
  if (fnRangeMatch) {
    const fnName = fnRangeMatch[1].toUpperCase();
    const targetRef = fnRangeMatch[2];
    const start = parseInt(fnRangeMatch[3]);
    const end = parseInt(fnRangeMatch[4]);
    
    let targetRowNum;
    if (targetRef.toLowerCase() === 'self') {
      targetRowNum = rowNumber;
    } else {
      targetRowNum = resolvePathToRow(targetRef, modelIndex, rowById, labelRow);
    }
    
    if (targetRowNum) {
      const from = colNum + start;
      const to = colNum + end;
      // If the range requires columns before the Excel window, fall back to cached
      // value rather than silently truncating (e.g. AVERAGE(self[-12:-1]) at week 3
      // would otherwise only average 2 weeks instead of 12).
      if (from < firstDateCol) return null;
      if (to >= firstDateCol && from <= to) {
        return `${fnName}(${cellAddr(targetRowNum, from)}:${cellAddr(targetRowNum, to)})`;
      }
    }
    return null;
  }
  
  // ── PERIOD_CONTAINS_DATES — translate to Excel SUMPRODUCT ──────
  // Handles both embedded cases (e.g. "row * MIN(PERIOD_CONTAINS_DATES([1,15]),1)")
  // and standalone expressions ("PERIOD_CONTAINS_DATES([1])").
  if (trimmed.includes('PERIOD_CONTAINS_DATES')) {
    const translated = replacePeriodContainsDates(trimmed, colNum);
    // Standalone expression — return SUMPRODUCT directly
    if (/^PERIOD_CONTAINS_DATES\s*\(/i.test(trimmed)) {
      return translated;
    }
    // Embedded — update trimmed so remaining row-path refs can be resolved below
    trimmed = translated;
  }
  
  // ── QUERY(...) — not translatable, use cached value ──────
  if (trimmed.startsWith('QUERY')) {
    return null;
  }

  // ── SUM_ROWS pre-processing ────────────────────────────────
  // Resolve SUM_ROWS([row1, row2, ...]) and SUM_ROWS([...])[-N] into
  // Excel SUM(cellRef1, cellRef2, ...) before further processing.
  if (/SUM_ROWS\s*\(/i.test(trimmed)) {
    const resolved = resolveSumRows(trimmed, colNum, firstDateCol, modelIndex, rowById, labelRow);
    if (resolved !== null) {
      // After resolving SUM_ROWS, the expression may be purely an Excel formula
      // or it may still contain other path references for arithmetic.
      // Check if it's a simple resolved expression (no more path tokens)
      const hasUnresolved = /[a-zA-Z_][a-zA-Z0-9_]*\.[a-zA-Z_][a-zA-Z0-9_.]*/g.test(resolved);
      if (!hasUnresolved) {
        return resolved;
      }
      // Otherwise pass the partially-resolved expression to the arithmetic handler
      return tryArithmeticFormula(resolved, colNum, rowNumber, firstDateCol, modelIndex, rowById, labelRow);
    }
    // If SUM_ROWS couldn't be resolved, fall back to cache
    return null;
  }
  
  // ── pathRef[-N] single offset (e.g. ending_cash[-1]) ────
  const pathOffsetMatch = trimmed.match(/^([a-zA-Z_][a-zA-Z0-9_.]*)\[(-?\d+)\]$/);
  if (pathOffsetMatch) {
    const targetPath = pathOffsetMatch[1];
    const offset = parseInt(pathOffsetMatch[2]);
    
    let targetRowNum;
    if (targetPath.toLowerCase() === 'self') {
      targetRowNum = rowNumber;
    } else {
      targetRowNum = resolvePathToRow(targetPath, modelIndex, rowById, labelRow);
    }
    if (targetRowNum) {
      const tc = colNum + offset;
      if (tc >= firstDateCol) return cellAddr(targetRowNum, tc);
    }
    return null;
  }
  
  // ── Bare path reference (e.g. "op_inflows.main.revenue") ─
  const barePathMatch = trimmed.match(/^([a-zA-Z_][a-zA-Z0-9_.]+)$/);
  if (barePathMatch) {
    const targetRowNum = resolvePathToRow(barePathMatch[1], modelIndex, rowById, labelRow);
    if (targetRowNum) return cellAddr(targetRowNum, colNum);
    return null;
  }
  
  // ── Arithmetic with path references ──────────────────────
  return tryArithmeticFormula(trimmed, colNum, rowNumber, firstDateCol, modelIndex, rowById, labelRow);
}

/**
 * Resolve a path/id string to its Excel row number.
 */
function resolvePathToRow(ref, modelIndex, rowById, labelRow) {
  if (rowById[ref]) return rowById[ref];
  
  const nodeByPath = modelIndex.byPath[ref];
  if (nodeByPath) {
    if (nodeByPath.kind === 'group') {
      const totalLabel = `Total ${nodeLabel(nodeByPath)}`;
      return labelRow[totalLabel] || rowById[nodeByPath.id];
    }
    return rowById[nodeByPath.id] || rowById[ref];
  }
  
  const nodeById = modelIndex.byId[ref];
  if (nodeById) {
    if (nodeById.kind === 'group') {
      const totalLabel = `Total ${nodeLabel(nodeById)}`;
      return labelRow[totalLabel] || rowById[nodeById.id];
    }
    return rowById[nodeById.id];
  }
  
  // Partial match: try last segment
  const parts = ref.split('.');
  const last = parts[parts.length - 1];
  if (last !== ref) {
    return rowById[last] || resolvePathToRow(last, modelIndex, rowById, labelRow);
  }
  
  return null;
}

/**
 * Try to convert an arithmetic expression with path tokens to Excel formula.
 */
function tryArithmeticFormula(expr, colNum, selfRowNum, firstDateCol, modelIndex, rowById, labelRow) {
  // Pre-process: convert any AVERAGE/MIN/MAX/SUM(ref[-N:-M]) sub-expressions to Excel
  // equivalents before the token loop, so arithmetic like AVERAGE(self[-4:-1])*1.33 works.
  // If the range extends before the Excel window, leave the match unresolved so the
  // bail-out check below triggers a fall-back to the cached hydrated value.
  const fnRangePattern = /(AVERAGE|MIN|MAX|SUM)\(\s*([a-zA-Z_][a-zA-Z0-9_.]*)\s*\[\s*(-?\d+)\s*:\s*(-?\d+)\s*\]\s*\)/gi;
  let processed = expr.replace(fnRangePattern, (match, fnName, targetRef, startStr, endStr) => {
    const start = parseInt(startStr);
    const end = parseInt(endStr);
    const targetRowNum = targetRef.toLowerCase() === 'self'
      ? selfRowNum
      : resolvePathToRow(targetRef, modelIndex, rowById, labelRow);
    if (targetRowNum) {
      const from = colNum + start;
      const to = colNum + end;
      if (from < firstDateCol) return match; // out-of-window → leave unresolved
      if (to >= firstDateCol && from <= to) {
        return `${fnName.toUpperCase()}(${cellAddr(targetRowNum, from)}:${cellAddr(targetRowNum, to)})`;
      }
    }
    return match; // leave unresolved; will be caught below
  });

  // If any function range pattern is still present (unresolved or out-of-window), bail out
  if (/(AVERAGE|MIN|MAX|SUM)\(\s*[a-zA-Z_][a-zA-Z0-9_.]*\s*\[\s*-?\d+\s*:\s*-?\d+\s*\]\s*\)/i.test(processed)) return null;

  const fnWasReplaced = processed !== expr;

  // Find all path-like tokens (including those with [offset])
  const tokenRegex = /([a-zA-Z_][a-zA-Z0-9_.]*(?:\.[a-zA-Z_][a-zA-Z0-9_]*)*)(?:\[(-?\d+)\])?/g;
  const tokens = [];
  let m;
  while ((m = tokenRegex.exec(processed)) !== null) {
    tokens.push({ full: m[0], path: m[1], offset: m[2] ? parseInt(m[2]) : 0 });
  }
  
  // Sort longest first to avoid partial replacement
  tokens.sort((a, b) => b.full.length - a.full.length);
  
  // Deduplicate
  const seen = new Set();
  const uniqueTokens = tokens.filter(t => {
    if (seen.has(t.full)) return false;
    seen.add(t.full);
    return true;
  });
  
  let formula = processed;
  let anyResolved = false;
  
  for (const token of uniqueTokens) {
    const upper = token.path.toUpperCase();
    if (['SUM', 'SUM_ROWS', 'AVERAGE', 'AVG', 'MIN', 'MAX', 'IF', 'ROUND', 'ABS', 'CHILDREN', 'FORMULA', 'SUMPRODUCT', 'DAY', 'MONTH'].includes(upper)) continue;
    if (/^\d+(\.\d+)?$/.test(token.path)) continue;
    
    let targetRowNum;
    if (token.path.toLowerCase() === 'self') {
      targetRowNum = selfRowNum;
    } else {
      targetRowNum = resolvePathToRow(token.path, modelIndex, rowById, labelRow);
    }
    
    if (targetRowNum) {
      const targetCol = colNum + token.offset;
      if (targetCol < firstDateCol) continue;
      const ref = cellAddr(targetRowNum, targetCol);
      formula = formula.replace(new RegExp(token.full.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), ref);
      anyResolved = true;
    }
  }
  
  if (anyResolved) {
    // Check for remaining unresolved multi-segment paths
    const remaining = formula.match(/[a-zA-Z_][a-zA-Z0-9_]*\.[a-zA-Z_][a-zA-Z0-9_.]*/g);
    if (!remaining || remaining.length === 0) {
      return formula;
    }
  }

  // If function range sub-expressions were replaced but no other path tokens needed
  // resolution (e.g. AVERAGE(self[-4:-1])*1.33), return the pre-processed formula
  // as long as no unresolved dot-path references remain.
  if (fnWasReplaced) {
    const remaining = formula.match(/[a-zA-Z_][a-zA-Z0-9_]*\.[a-zA-Z_][a-zA-Z0-9_.]*/g);
    if (!remaining || remaining.length === 0) {
      return formula;
    }
  }
  
  return null;
}

/**
 * Resolve all SUM_ROWS([row1, row2, ...]) and SUM_ROWS([...])[-N] occurrences
 * in an expression to Excel SUM(cellRef, cellRef, ...) notation.
 *
 * Handles patterns like:
 *   SUM_ROWS([revenue_a, revenue_b])                    → SUM(C10,C12)
 *   SUM_ROWS([revenue_a, revenue_b])[-1]                → SUM(B10,B12)
 *   SUM_ROWS([revenue_a, revenue_b]) * some_driver_ref  → SUM(C10,C12) * C54
 */
function resolveSumRows(expr, colNum, firstDateCol, modelIndex, rowById, labelRow) {
  // Match SUM_ROWS([...]) with optional [-N] offset
  const pattern = /SUM_ROWS\s*\(\s*\[([^\]]*)\]\s*\)(?:\s*\[\s*(-?\d+)\s*\])?/gi;
  let result = expr;
  let anyResolved = false;

  result = result.replace(pattern, (match, rowListStr, offsetStr) => {
    const offset = offsetStr ? parseInt(offsetStr) : 0;
    const targetCol = colNum + offset;

    if (targetCol < firstDateCol) return match; // can't resolve — out of range

    // Parse comma-separated row ids, stripping quotes and whitespace
    const rowIds = rowListStr
      .split(',')
      .map(id => id.trim().replace(/['"]/g, ''))
      .filter(id => id.length > 0);

    const cellRefs = [];
    for (const rowId of rowIds) {
      const rn = resolvePathToRow(rowId, modelIndex, rowById, labelRow);
      if (rn) {
        cellRefs.push(cellAddr(rn, targetCol));
      }
    }

    if (cellRefs.length > 0) {
      anyResolved = true;
      return `SUM(${cellRefs.join(',')})`;
    }

    return match; // couldn't resolve any rows
  });

  return anyResolved ? result : null;
}

// ─────────────────────────────────────────────────────────────
// PERIOD_CONTAINS_DATES → Excel SUMPRODUCT translation
// ─────────────────────────────────────────────────────────────

/**
 * Replace every PERIOD_CONTAINS_DATES(...) call in `expr` with an Excel
 * SUMPRODUCT formula that counts how many days in the 7-day period (starting
 * at the date cell for the current column) match the given day/month criteria.
 *
 * Supported forms (same as the backend formula engine):
 *   PERIOD_CONTAINS_DATES([days])            – matches those day-of-month values in any month
 *   PERIOD_CONTAINS_DATES([months], [days])  – matches (month ∈ months) AND (day ∈ days)
 *
 * The date cell is taken from row 5 (the header date row) at colNum, which
 * holds the ISO date for this period as a Date object.
 * The 7-day window {0,1,2,3,4,5,6} mirrors the backend's assumption.
 */
function replacePeriodContainsDates(expr, colNum) {
  const dateRef = cellAddr(5, colNum); // Row 5 = date header row for this column
  const offsets = '{0,1,2,3,4,5,6}';  // 7-day period window

  const pattern = /PERIOD_CONTAINS_DATES\s*\(\s*\[([^\]]*)\]\s*(?:,\s*\[([^\]]*)\]\s*)?\)/gi;
  return expr.replace(pattern, (match, firstArr, secondArr) => {
    let paymentMonths = null;
    let paymentDays;

    if (secondArr !== undefined) {
      // Two-array form: PERIOD_CONTAINS_DATES([months], [days])
      paymentMonths = firstArr.split(',')
        .map(m => parseInt(m.trim()))
        .filter(m => !isNaN(m) && m >= 1 && m <= 12);
      paymentDays = secondArr.split(',')
        .map(d => parseInt(d.trim()))
        .filter(d => !isNaN(d) && d >= 1 && d <= 31);
    } else {
      // Single-array form: PERIOD_CONTAINS_DATES([days])
      paymentDays = firstArr.split(',')
        .map(d => parseInt(d.trim()))
        .filter(d => !isNaN(d) && d >= 1 && d <= 31);
    }

    if (paymentDays.length === 0) return '0';

    const dayExprs = paymentDays.map(d => `(DAY(${dateRef}+${offsets})=${d})`);

    if (paymentMonths && paymentMonths.length > 0) {
      // Each (month, day) combination becomes a separate term so counts are additive
      const terms = [];
      for (const m of paymentMonths) {
        for (const dc of dayExprs) {
          terms.push(`${dc}*(MONTH(${dateRef}+${offsets})=${m})`);
        }
      }
      return `SUMPRODUCT((${terms.join('+')})*1)`;
    }

    // Day-only form
    if (dayExprs.length === 1) {
      return `SUMPRODUCT(${dayExprs[0]}*1)`;
    }
    return `SUMPRODUCT((${dayExprs.join('+')})*1)`;
  });
}

// ─────────────────────────────────────────────────────────────
// Dynamic period key resolver (browser equivalent of backend helper)
// ─────────────────────────────────────────────────────────────

/**
 * Resolve dynamic override keys like @F1, @F2, @A-1, WK1 to actual formulas.
 * Returns the override formula if the current date matches, or null.
 */
function resolveDynamicOverride(overrides, dateStr, allDates, actualDates) {
  if (!overrides || typeof overrides !== 'object') return null;

  const actualPeriodCount = allDates.filter(d => actualDates.has(d)).length;
  const periodIndex = allDates.indexOf(dateStr);
  if (periodIndex < 0) return null;

  for (const [key, formula] of Object.entries(overrides)) {
    // Skip direct date keys — those are handled before this function is called
    if (/^\d{4}-\d{2}-\d{2}$/.test(key)) continue;

    let targetIndex = null;

    // @F1, @F2, etc. — forecast period offsets (F1 = first forecast period)
    const fMatch = key.match(/^@F(\d+)$/i);
    if (fMatch) {
      const num = parseInt(fMatch[1]);
      if (num >= 1) targetIndex = actualPeriodCount + (num - 1);
    }

    // @A-1, @A-2, etc. — counting back from last actual period
    const aMatch = key.match(/^@A(-\d+)$/i);
    if (aMatch) {
      targetIndex = actualPeriodCount + parseInt(aMatch[1]);
    }

    // Legacy WK notation: WK1 = first forecast period
    const wkMatch = key.match(/^WK(\d+)$/i);
    if (wkMatch) {
      targetIndex = actualPeriodCount + (parseInt(wkMatch[1]) - 1);
    }

    if (targetIndex !== null && targetIndex === periodIndex && targetIndex >= 0 && targetIndex < allDates.length) {
      return formula;
    }
  }
  return null;
}

// ─────────────────────────────────────────────────────────────
// Populate sheet with values and formulas
// ─────────────────────────────────────────────────────────────

function populateSheet(ws, data, rowById) {
  const dateCol = buildDateCol(ws);
  const labelRow = findLabelRows(ws);
  const modelIndex = indexNodes(data.model);

  const actualDates = new Set(data.calendar?.periods?.actual ?? []);
  const forecastDates = data.calendar?.periods?.forecast ?? [];
  const allDates = [
    ...(data.calendar?.periods?.actual ?? []),
    ...forecastDates
  ];
  const allDateCols = Object.values(dateCol).map(Number);
  const firstDateCol = allDateCols.length > 0 ? Math.min(...allDateCols) : 3;

  // ── 1. Populate summary ref-child rows (=OriginalRow formulas) ──
  for (const [nodeId, rowNumber] of Object.entries(rowById)) {
    if (!nodeId.startsWith('_ref_')) continue;

    // Extract original row ID: _ref_{groupId}_{rowId}
    const parts = nodeId.split('_ref_')[1].split('_');
    // groupId might contain underscores, so find the original row by trying the last segment first
    // Actually we need a better approach: find the original row ID by checking rowById
    const originalRowId = findOriginalRowId(parts, rowById);
    const originalRowNum = originalRowId ? rowById[originalRowId] : null;

    if (originalRowNum) {
      // Apply same number format as the original row
      const originalNode = modelIndex.byId[originalRowId];
      if (originalNode) {
        applyRowFormat(ws, rowNumber, originalNode.type || originalNode.valueType, dateCol);
      }

      // Each cell = formula reference to the original row's cell
      for (const [, col] of Object.entries(dateCol)) {
        const colNum = Number(col);
        ws.getCell(rowNumber, colNum).value = { formula: cellAddr(originalRowNum, colNum) };
      }
    }
  }

  // ── 2a. Populate display-group subtotal rows ──
  for (const rowNumber of DISPLAY_GROUP_SUBTOTAL_ROWS) {
    const childRowNums = ws._displayGroupSubtotalMap?.[rowNumber];
    if (childRowNums && childRowNums.length > 0) {
      for (const [, col] of Object.entries(dateCol)) {
        const colNum = Number(col);
        const refs = childRowNums.map(rn => cellAddr(rn, colNum));
        ws.getCell(rowNumber, colNum).value = { formula: `SUM(${refs.join(',')})` };
      }
    }
  }

  // ── 2. Populate synthetic Total rows ──
  for (const rowNumber of SYN_TOTAL_ROWS) {
    const label = ws.getCell(rowNumber, 2).value?.toString();
    if (!label) continue;
    const groupLabel = label.replace(/^Total\s+/, '').trim();
    const groupNode = modelIndex.byLabel[groupLabel];

    // Check if this is a summary total with pre-mapped children
    const summaryChildren = ws._summaryTotalMap?.[rowNumber];
    if (summaryChildren && summaryChildren.length > 0) {
      for (const [, col] of Object.entries(dateCol)) {
        const colNum = Number(col);
        const refs = summaryChildren.map(rn => cellAddr(rn, colNum));
        ws.getCell(rowNumber, colNum).value = { formula: `SUM(${refs.join(',')})` };
      }
      continue;
    }

    // Check if this total is backed by display-group subtotals + ungrouped rows
    const displayGroupChildren = ws._displayGroupTotalMap?.[rowNumber];
    if (displayGroupChildren && displayGroupChildren.length > 0) {
      for (const [, col] of Object.entries(dateCol)) {
        const colNum = Number(col);
        const refs = displayGroupChildren.map(rn => cellAddr(rn, colNum));
        ws.getCell(rowNumber, colNum).value = { formula: `SUM(${refs.join(',')})` };
      }
      continue;
    }

    if (!groupNode) continue;

    // Non-summary totals: SUM of child row numbers
    const childRowNums = [];
    for (const c of groupNode.children || []) {
      if (c._isVirtualDriverGroup) continue;
      if (c._isDriverDisplay) continue;
      if (c._isRefChild) continue; // skip ref children for non-summary (they have their own Total)
      
      if (c.kind === 'row') {
        const rn = rowById[c.id];
        if (rn) childRowNums.push(rn);
      }
    }

    if (childRowNums.length > 0) {
      for (const [, col] of Object.entries(dateCol)) {
        const colNum = Number(col);
        const refs = childRowNums.map(rn => cellAddr(rn, colNum));
        ws.getCell(rowNumber, colNum).value = { formula: `SUM(${refs.join(',')})` };
      }
    } else if (groupNode.cache) {
      for (const [dateStr, col] of Object.entries(dateCol)) {
        const v = groupNode.cache[dateStr];
        if (v !== undefined && v !== null) {
          ws.getCell(rowNumber, Number(col)).value = v;
        }
      }
    }
  }

  // ── 3. Populate regular rows by ID ──────────────────────
  for (const [nodeId, rowNumber] of Object.entries(rowById)) {
    // Skip ref rows (already handled in section 1)
    if (nodeId.startsWith('_ref_')) continue;
    
    // Skip synthetic total rows (already handled in section 2)
    if (SYN_TOTAL_ROWS.has(rowNumber)) continue;

    // Skip display-group subtotal rows (already handled in section 2a)
    if (DISPLAY_GROUP_SUBTOTAL_ROWS.has(rowNumber)) continue;

    const node = modelIndex.byId[nodeId] || modelIndex.byPath[nodeId];
    if (!node) continue;

    // Apply number format
    applyRowFormat(ws, rowNumber, node.type || node.valueType, dateCol);

    for (const [dateStr, col] of Object.entries(dateCol)) {
      const colNum = Number(col);
      const cell = ws.getCell(rowNumber, colNum);
      const isActual = actualDates.has(dateStr);

      // Determine the expression for this period
      const config = isActual ? node.actuals : node.forecast;
      let expression = null;
      if (config) {
        if (Array.isArray(config)) {
          // New array format: last element repeats for remaining periods
          const periodList = isActual ? [...actualDates] : forecastDates;
          const idx = periodList.indexOf(dateStr);
          if (idx >= 0) {
            const arrIdx = Math.min(idx, config.length - 1);
            const val = config[arrIdx];
            expression = val != null ? String(val) : null;
          }
        } else if (typeof config === 'object' && config.default) {
          // 1. Exact date override
          expression = config.overrides?.[dateStr] || null;
          // 2. Dynamic period key override (@F1, @A-1, WK1, etc.)
          if (!expression && config.overrides) {
            expression = resolveDynamicOverride(config.overrides, dateStr, allDates, actualDates);
          }
          // 3. Fall back to default
          if (!expression) {
            expression = config.default;
          }
        } else if (typeof config === 'string') {
          expression = config;
        }
      }

      // Strip FORMULA() wrapper
      if (typeof expression === 'string' && expression.startsWith('FORMULA(') && expression.endsWith(')')) {
        expression = expression.slice(8, -1).trim();
      }

      // Skip raw-value configs (series inputs) — just use cache
      const isRawValues = config && config._isRawValues;

      // Try to convert expression to Excel formula
      if (expression && !isRawValues) {
        const excelFormula = convertExprToExcelFormula(
          expression, node, rowNumber, colNum, dateCol,
          modelIndex, rowById, labelRow, firstDateCol
        );
        if (excelFormula) {
          cell.value = { formula: excelFormula };
          continue;
        }
      }

      // Fall back to cached values
      if (node.cache) {
        const val = node.cache[dateStr];
        if (val !== undefined && val !== null) {
          cell.value = val;
          continue;
        }
      }

      // Legacy: node.values
      if (node.values && node.values[dateStr] !== undefined) {
        cell.value = node.values[dateStr];
      }
    }
  }
}

/**
 * Helper: given the suffix parts from a _ref_ key, find the original row ID in rowById.
 * Key format: _ref_{groupId}_{rowId} — but both groupId and rowId may contain underscores.
 * Strategy: try progressively longer prefixes as the groupId.
 */
function findOriginalRowId(parts, rowById) {
  // Rejoin all parts with underscore
  const joined = parts.join('_');
  
  // Try splitting at each position to find a rowId that exists in rowById
  for (let i = 1; i < parts.length; i++) {
    const candidateRowId = parts.slice(i).join('_');
    if (rowById[candidateRowId]) {
      return candidateRowId;
    }
  }
  
  // Fallback: try the whole string
  if (rowById[joined]) return joined;
  
  return null;
}

// ─────────────────────────────────────────────────────────────
// Cover Sheet
// ─────────────────────────────────────────────────────────────

const COVER_COLORS = {
  NAVY: 'FF1F4E79',
  NAVY_LIGHT: 'FF2B6CB0',
  BLUE_ACCENT: 'FF4A90E2',
  GRAY_TEXT: 'FF6B7785',
  GRAY_LIGHT: 'FFF7F9FC',
  GRAY_BORDER: 'FFD0D7E2',
  WHITE: 'FFFFFFFF',
};

async function buildCoverSheet(wb, data, metadata) {
  const { primary, secondary, accent } = buildBrandingColors(metadata?.branding);
  const colors = {
    ...COVER_COLORS,
    NAVY: primary,
    NAVY_LIGHT: secondary,
    BLUE_ACCENT: accent,
  };
  const logoUrl = metadata?.branding?.logoDownloadUrl || metadata?.branding?.logoPresignedUrl || null;
  const ws = wb.addWorksheet('Cover', {
    properties: { tabColor: { argb: primary } }
  });

  ws.views = [{ showGridLines: false }];

  // Column widths
  ws.getColumn(1).width = 2;
  ws.getColumn(2).width = 75;
  ws.getColumn(3).width = 58;

  const workspaceName = metadata?.workspaceName || data.workspaceName || data.workspace_name || '';
  const forecastName = metadata?.forecastName || 'Cash Flow Forecast';
  const calendar = data.calendar || {};
  const { actualPeriods, forecastPeriods, granLabel, subtitlePeriods } = getForecastSubtitleInfo(calendar);
  const totalPeriods = actualPeriods.length + forecastPeriods.length;
  const pullDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  const allPeriods = [...actualPeriods, ...forecastPeriods];
  const startDate = subtitlePeriods[0] || allPeriods[0] || '';
  const endDate = subtitlePeriods[subtitlePeriods.length - 1] || allPeriods[allPeriods.length - 1] || '';

  // Helper to add an empty row
  const addEmpty = (count = 1) => { for (let i = 0; i < count; i++) ws.addRow([]); };

  // ── Top spacer
  addEmpty(1);

  // ── Navy accent bar starts at B2
  for (let r = 0; r < 2; r++) {
    const row = ws.addRow([]);
    for (let c = 2; c <= 3; c++) {
      row.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: colors.NAVY } };
    }
  }

  // ── Spacer
  addEmpty(1);

  // ── Workspace name (row 8)
  const wsRow = ws.addRow(['', workspaceName]);
  wsRow.getCell(2).font = { name: 'Calibri', size: 13, color: { argb: colors.GRAY_TEXT } };
  wsRow.getCell(2).alignment = { vertical: 'middle', horizontal: 'left' };
  wsRow.height = 22;

  // ── Spacer
  addEmpty(1);

  // ── Report title (row 10)
  const titleRow = ws.addRow(['', forecastName]);
  titleRow.getCell(2).font = { name: 'Calibri', size: 26, bold: true, color: { argb: colors.NAVY } };
  titleRow.getCell(2).alignment = { vertical: 'middle', horizontal: 'left' };
  titleRow.height = 40;

  // ── Subtitle (row 11)
  const subtitleText = `${forecastPeriods.length}-${granLabel} Cash Flow Model`;
  const subtitleRow = ws.addRow(['', subtitleText]);
  subtitleRow.getCell(2).font = { name: 'Calibri', size: 14, color: { argb: colors.GRAY_TEXT } };
  subtitleRow.getCell(2).alignment = { vertical: 'middle', horizontal: 'left' };
  subtitleRow.height = 24;

  // ── Spacer
  addEmpty(2);

  // ── Thin divider line (row 14)
  const divRow = ws.addRow([]);
  for (let c = 2; c <= 3; c++) {
    divRow.getCell(c).border = { bottom: { style: 'thin', color: { argb: colors.GRAY_BORDER } } };
  }

  // ── Spacer
  addEmpty(1);

  // ── Metadata rows
  const metaFont = { name: 'Calibri', size: 11, color: { argb: colors.GRAY_TEXT } };
  const metaValueFont = { name: 'Calibri', size: 11, color: { argb: 'FF333333' } };

  const metaItems = [
    ['Report Date', pullDate],
    ['Granularity', `${granLabel}ly`],
    ['Period Range', `${formatDatePretty(startDate)}  —  ${formatDatePretty(endDate)}`],
    ['Actual Periods', `${actualPeriods.length}`],
    ['Forecast Periods', `${forecastPeriods.length}`],
    ['Total Periods', `${totalPeriods}`],
  ];

  if (calendar.forecast_start) {
    metaItems.push(['Forecast Start', formatDatePretty(calendar.forecast_start)]);
  }
  if (data.case && data.case !== 'BASE') {
    metaItems.push(['Scenario', data.case]);
  }

  metaItems.forEach(([label, value]) => {
    const row = ws.addRow(['', label, value]);
    row.getCell(2).font = metaFont;
    row.getCell(3).font = metaValueFont;
    row.height = 20;
  });

  // ── Spacer
  addEmpty(2);

  // ── Thin divider line
  const divRow2 = ws.addRow([]);
  for (let c = 2; c <= 3; c++) {
    divRow2.getCell(c).border = { bottom: { style: 'thin', color: { argb: colors.GRAY_BORDER } } };
  }

  addEmpty(1);


  // ── Logo (C5:C10) — right-aligned, aspect-ratio-preserving
  if (logoUrl) {
    try {
      const useCredentials = logoUrl.includes('/api/orgs/logo-file/');
      const resp = await fetch(logoUrl, useCredentials ? { credentials: 'include' } : undefined);
      if (resp.ok) {
        const blob = await resp.blob();
        const mimeType = (blob.type || resp.headers.get('content-type') || 'image/png').split(';')[0].toLowerCase();
        if (!['image/png', 'image/jpeg', 'image/jpg', 'image/gif'].includes(mimeType)) {
          console.warn('Skipping unsupported logo mime type for Excel export:', mimeType);
        } else {
          const ext = mimeType.includes('jpeg') || mimeType.includes('jpg') ? 'jpeg'
            : mimeType.includes('gif') ? 'gif' : 'png';
          const arrayBuffer = await blob.arrayBuffer();
          // Safe base64 encode — avoid spread-on-large-array stack overflow
          const bytes = new Uint8Array(arrayBuffer);
          let binary = '';
          for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
          const base64 = btoa(binary);
          const imageId = wb.addImage({ base64: `data:${mimeType};base64,${base64}`, extension: ext });

          // Measure the image so we can fit it into a fixed box without distortion.
          const objectUrl = URL.createObjectURL(blob);
          try {
            const dimensions = await new Promise((resolve, reject) => {
              const img = new Image();
              img.onload = () => resolve({
                width: img.naturalWidth || 1,
                height: img.naturalHeight || 1,
              });
              img.onerror = () => reject(new Error('Unable to measure logo dimensions'));
              img.src = objectUrl;
            });

            const maxWidthPx = 180;
            const maxHeightPx = 100;
            const scale = Math.min(
              maxWidthPx / dimensions.width,
              maxHeightPx / dimensions.height,
              1
            );
            const fittedWidth = Math.round(dimensions.width * scale);
            const fittedHeight = Math.round(dimensions.height * scale);

            // Position the logo inside the white space at rows 5-10, right-aligned in column C.
            const columnCPixels = Math.round((ws.getColumn(3).width || 8.43) * 7 + 5);
            const boxTopRow = 4; // Excel row 5 in 0-based coordinates
            const boxHeightPixels = [5, 6, 7, 8, 9, 10]
              .reduce((sum, rowNum) => sum + Math.round(((ws.getRow(rowNum).height || 15) * 96) / 72), 0);
            const rightPaddingPx = 0;
            const topOffsetPx = Math.max(0, Math.round((boxHeightPixels - fittedHeight) / 2));
            const leftOffsetPx = Math.max(0, columnCPixels - fittedWidth - rightPaddingPx);
            const EMUS_PER_PIXEL = 9525;

            ws.addImage(imageId, {
              tl: {
                col: 2,
                row: boxTopRow,
                nativeCol: 2,
                nativeRow: boxTopRow,
                nativeColOff: Math.round(leftOffsetPx * EMUS_PER_PIXEL),
                nativeRowOff: Math.round(topOffsetPx * EMUS_PER_PIXEL),
              },
              ext: {
                width: fittedWidth,
                height: fittedHeight,
              },
              editAs: 'oneCell',
            });
          } finally {
            URL.revokeObjectURL(objectUrl);
          }
        }
      } else {
        console.warn('Logo fetch failed for Excel export:', resp.status, resp.statusText);
      }
    } catch (e) {
      console.warn('Logo embed failed:', e?.message);
    }
  }

  // Print settings
  ws.pageSetup = {
    orientation: 'landscape',
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    margins: {
      left: 0.3,
      right: 0.3,
      top: 0.3,
      bottom: 0.3,
      header: 0,
      footer: 0,
    },
  };

  return ws;
}

// ─────────────────────────────────────────────────────────────
// Summary Sheet
// ─────────────────────────────────────────────────────────────

const SUMMARY_COLORS = {
  NAVY: 'FF1F4E79',
  GREEN_FILL: 'FFE5F3E8',
  GREEN_FONT: 'FF118C4F',
  RED_FILL: 'FFFCE8E8',
  RED_FONT: 'FFCC3333',
  BLUE_FILL: 'FFEBF3FF',
  BLUE_FONT: 'FF466391',
  PURPLE_FILL: 'FFF2EAFE',
  PURPLE_FONT: 'FF7A3DB8',
  GRAY_TEXT: 'FF6B7785',
  GRAY_LIGHT: 'FFF7F9FC',
  GRAY_BORDER: 'FFD0D7E2',
  WHITE: 'FFFFFFFF',
};

/**
 * Build the Summary sheet with a chart-like table referencing the Model sheet.
 * Shows Cash Inflows (bar), Cash Outflows (bar), Ending Cash Balance (line) 
 * with formulas pointing to the Model sheet's rows.
 */
function buildSummarySheet(wb, data, modelWs, rowById, metadata) {
  const { primary, secondary, accent } = buildBrandingColors(metadata?.branding);
  const primaryLight = lightTint(primary, 0.15);
  const colors = {
    ...SUMMARY_COLORS,
    NAVY: primary,
    BLUE_FILL: primaryLight,
    BLUE_FONT: 'FF404040',
  };
  const ws = wb.addWorksheet('Summary', {
    properties: { tabColor: { argb: primary } }
  });

  const calendar = data.calendar || {};
  const { actualPeriods, forecastPeriods, subtitle } = getForecastSubtitleInfo(calendar);
  const allPeriods = [...actualPeriods, ...forecastPeriods];
  const actualCount = actualPeriods.length;

  if (allPeriods.length === 0) return ws;

  const LABEL_COL = 2;
  const FIRST_DATE_COL = 3;
  const lastDateCol = FIRST_DATE_COL + allPeriods.length - 1;

  ws.views = [{ showGridLines: false }];

  // Column widths
  ws.getColumn(1).width = 2;
  ws.getColumn(2).width = 23;
  for (let c = FIRST_DATE_COL; c <= lastDateCol; c++) {
    ws.getColumn(c).width = 10;
  }

  // ── Row 1: Title
  const titleRow = ws.addRow(['', 'Forecast Summary']);
  titleRow.getCell(2).font = { name: 'Calibri', size: 16, bold: true, color: { argb: colors.NAVY } };
  titleRow.height = 26;
  titleRow.getCell(2).alignment = { horizontal: 'left', vertical: 'middle' };

  // ── Row 2: Subtitle
  const subtitleRow = ws.addRow(['', subtitle]);
  subtitleRow.getCell(2).font = { name: 'Calibri', size: 10, italic: true, color: { argb: colors.GRAY_TEXT } };
  subtitleRow.height = 18;
  subtitleRow.getCell(2).alignment = { horizontal: 'left', vertical: 'middle' };

  // ── Row 3: blank
  ws.addRow([]);

  // ── Row 4: Actual/Forecast labels
  const periodTypeRow = ws.addRow(['', '']);
  for (let i = 0; i < allPeriods.length; i++) {
    const cell = periodTypeRow.getCell(FIRST_DATE_COL + i);
    if (i < actualCount) {
      cell.value = 'Actual';
      cell.font = { name: 'Calibri', size: 10, bold: true, italic: true, color: { argb: 'FF404040' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8E8E8' } };
    } else {
      cell.value = 'Forecast';
      cell.font = { name: 'Calibri', size: 10, bold: true, italic: true, color: { argb: 'FF404040' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: primaryLight } };
    }
    cell.alignment = { horizontal: 'center' };
  }

  // ── Row 5: Period dates
  const formatPeriodDate = (dateStr) => {
    if (!dateStr) return '';
    const dt = new Date(dateStr + 'T00:00:00Z');
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  };

  const dateRow = ws.addRow(['', '']);
  for (let i = 0; i < allPeriods.length; i++) {
    const cell = dateRow.getCell(FIRST_DATE_COL + i);
    cell.value = formatPeriodDate(allPeriods[i]);
    cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FF737373' } };
    cell.alignment = { horizontal: 'center' };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: colors.WHITE } };
    cell.border = { bottom: { style: 'thin', color: { argb: colors.GRAY_BORDER } } };
  }
  dateRow.getCell(LABEL_COL).border = { bottom: { style: 'thin', color: { argb: colors.GRAY_BORDER } } };

  // ── Find the Model sheet rows we need to reference
  // We need: op_inflows total, op_outflows total, ending_cash
  // These are in the summary section of the model
  const modelDateCol = buildDateCol(modelWs);
  const modelLabelRow = findLabelRows(modelWs);
  const modelIndex = indexNodes(data.model);

  // Look up row numbers on Model sheet for summary items
  const findModelRow = (id) => {
    // Try rowById first (stored during layout)
    if (rowById[id]) return rowById[id];
    // Try summary path
    const summaryPath = `summary.${id}`;
    if (rowById[summaryPath]) return rowById[summaryPath];
    // Try label lookup for totals
    const node = modelIndex.byId[id];
    if (node) {
      const totalLabel = `Total ${nodeLabel(node)}`;
      if (modelLabelRow[totalLabel]) return modelLabelRow[totalLabel];
    }
    return null;
  };

  const inflowsModelRow = findModelRow('op_inflows');
  const outflowsModelRow = findModelRow('op_outflows');
  const endingCashModelRow = findModelRow('ending_cash');
  const beginningCashModelRow = findModelRow('beginning_cash');
  const netChangeModelRow = findModelRow('change');
  const financingModelRow = findModelRow('financing');

  const MODEL_SHEET = 'Model';
  const currencyFmt = '$#,##0_);[Red]($#,##0)';
  const chartCurrencyFmt = '$#,##0.0_);($#,##0.0)';

  // Helper to create a row with formula references to the Model sheet
  const addFormulaRow = (label, modelRow, fillArgb, fontArgb, options = {}) => {
    const row = ws.addRow(['', label]);
    row.getCell(LABEL_COL).font = { name: 'Calibri', size: 10, bold: true, color: { argb: fontArgb } };
    row.getCell(LABEL_COL).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillArgb } };
    row.getCell(LABEL_COL).alignment = { vertical: 'middle' };
    row.height = 20;

    for (let i = 0; i < allPeriods.length; i++) {
      const col = FIRST_DATE_COL + i;
      const cell = row.getCell(col);

      if (modelRow) {
        // Find which column on the Model sheet this date maps to
        const dateStr = allPeriods[i];
        const modelCol = modelDateCol[dateStr];
        if (modelCol) {
          const modelRef = `'${MODEL_SHEET}'!${cellAddr(modelRow, Number(modelCol))}`;
          if (options.abs) {
            cell.value = { formula: `ABS(${modelRef})` };
          } else {
            cell.value = { formula: modelRef };
          }
        }
      }

      cell.numFmt = currencyFmt;
      cell.font = { name: 'Calibri', size: 10, color: { argb: fontArgb } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillArgb } };
      cell.alignment = { horizontal: 'right', vertical: 'middle' };
    }

    return row;
  };

  const cashRowFont = isLightColor(primary) ? 'FF1A1A1A' : 'FFFFFFFF';

  ws.addRow([]);
  const beginningCashRow = addFormulaRow('Beginning Cash Balance', beginningCashModelRow, primary, cashRowFont);
  ws.addRow([]);
  const inflowsRow = addFormulaRow('Cash Inflows', inflowsModelRow, colors.GREEN_FILL, colors.GREEN_FONT);
  const outflowsRow = addFormulaRow('Cash Outflows', outflowsModelRow, colors.RED_FILL, colors.RED_FONT);
  const financingRow = addFormulaRow('Financing', financingModelRow, colors.GRAY_LIGHT, colors.GRAY_TEXT);
  ws.addRow([]);
  const endingCashRow = addFormulaRow('Ending Cash Balance', endingCashModelRow, primary, cashRowFont);
  ws.addRow([]);
  const netChangeRow = addFormulaRow('Net Change Cash', netChangeModelRow, colors.WHITE, colors.BLUE_FONT);

  for (const row of [beginningCashRow, inflowsRow, outflowsRow, financingRow, netChangeRow, endingCashRow]) {
    for (let c = LABEL_COL; c <= lastDateCol; c++) {
      row.getCell(c).border = { bottom: { style: 'thin', color: { argb: colors.WHITE } } };
    }
  }

  // Fixed helper rows for the template chart source range.
  for (let c = FIRST_DATE_COL; c <= lastDateCol; c++) {
    const colLetter = colToLetter(c);
    const helper57 = ws.getCell(57, c);
    helper57.value = {
      formula: `${colLetter}9/1000+IF(${colLetter}11>0,${colLetter}11/1000,0)`
    };
    helper57.numFmt = chartCurrencyFmt;

    const helper58 = ws.getCell(58, c);
    helper58.value = {
      formula: `-${colLetter}10/1000+IF(${colLetter}11<=0,-${colLetter}11/1000,0)`
    };
    helper58.numFmt = chartCurrencyFmt;

    const helper59 = ws.getCell(59, c);
    helper59.value = {
      formula: `${colLetter}13/1000`
    };
    helper59.numFmt = chartCurrencyFmt;
  }

  // Make helper rows invisible (white-on-white) — chart still reads them, they won't print/show
  for (const rowNum of [57, 58, 59]) {
    const r = ws.getRow(rowNum);
    for (let c = 1; c <= lastDateCol; c++) {
      const cell = r.getCell(c);
      cell.font = { color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
    }
  }

  // Print settings — exclude helper rows from print area
  const lastVisibleRow = ws.rowCount > 56 ? 56 : ws.rowCount;
  ws.pageSetup = {
    orientation: 'landscape',
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    printArea: `A1:${colToLetter(lastDateCol)}${lastVisibleRow}`,
    margins: {
      left: 0.3,
      right: 0.3,
      top: 0.3,
      bottom: 0.3,
      header: 0,
      footer: 0,
    },
  };

  return {
    ws,
    chartSourceData: {
      allPeriods,
      actualCount,
      inflowsModelRow,
      outflowsModelRow,
      endingCashModelRow,
      modelDateCol,
      inflowsSummaryRow: inflowsRow.number,
      outflowsSummaryRow: outflowsRow.number,
      endingCashSummaryRow: endingCashRow.number,
    }
  };
}

// ─────────────────────────────────────────────────────────────
// Visuals Sheet
// ─────────────────────────────────────────────────────────────

/**
 * Extract the top N rows from a section, sorted by absolute total value.
 * Returns { topRows: [{row, absTotal}], groupCache: {dateStr: value} }
 */
function extractTopRows(model, sectionId, allPeriods, maxRows = 4) {
  const section = (model || []).find(s => s.id === sectionId);
  if (!section) return { topRows: [], groupCache: {} };

  const mainGroup = (section.children || []).find(g => g.kind === 'group' && g.id === 'main');
  if (!mainGroup) return { topRows: [], groupCache: {} };

  const dataRows = (mainGroup.children || []).filter(
    c => c.kind === 'row' && !c._isVirtualDriverGroup && !c._isRefChild && !c._isDriverDisplay
  );

  const rowTotals = dataRows.map(row => {
    let absTotal = 0;
    for (const dateStr of allPeriods) {
      const val = row.cache?.[dateStr];
      if (val != null) absTotal += Math.abs(Number(val));
    }
    return { row, absTotal };
  });

  rowTotals.sort((a, b) => b.absTotal - a.absTotal);

  const groupCache = {};
  if (mainGroup.cache) {
    for (const dateStr of allPeriods) {
      groupCache[dateStr] = mainGroup.cache[dateStr] ?? 0;
    }
  } else {
    for (const dateStr of allPeriods) {
      let sum = 0;
      for (const rt of rowTotals) {
        const val = rt.row.cache?.[dateStr];
        if (val != null) sum += Number(val);
      }
      groupCache[dateStr] = sum;
    }
  }

  return { topRows: rowTotals.slice(0, maxRows), groupCache };
}

/**
 * Build the Visuals sheet with chart source data for revenue, inflows, and
 * outflows breakdown.  Data is placed in B72:Y92 to feed the pre-built
 * template charts:
 *   Row 72        — calendar spine (dates)
 *   Rows 74-78    — revenue breakdown (top 4 + All Other)
 *   Rows 81-85    — inflows breakdown
 *   Rows 88-92    — outflows breakdown
 */
function buildVisualsSheet(wb, data, metadata) {
  const { primary } = buildBrandingColors(metadata?.branding);
  const ws = wb.addWorksheet('Visuals', {
    properties: { tabColor: { argb: primary } }
  });

  const calendar = data.calendar || {};
  const { subtitle } = getForecastSubtitleInfo(calendar);
  const actualPeriods = calendar.periods?.actual || [];
  const forecastPeriods = calendar.periods?.forecast || [];
  const allPeriods = [...actualPeriods, ...forecastPeriods];

  if (allPeriods.length === 0) return ws;

  const LABEL_COL = 2;
  const FIRST_DATE_COL = 3;
  const periodCount = Math.min(allPeriods.length, 23);
  const lastDateCol = FIRST_DATE_COL + periodCount - 1;

  const CALENDAR_ROW = 72;
  const REVENUE_START = 74;
  const INFLOWS_START = 81;
  const OUTFLOWS_START = 88;
  const FINANCING_ROW = 95;

  const DATA_FMT = '#,##0.0_);(#,##0.0)';
  const WHITE = 'FFFFFFFF';
  const whiteFont = { color: { argb: WHITE } };
  const whiteFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: WHITE } };

  // ── Visible header area (rows 1-3) ────────────────────────
  ws.views = [{ showGridLines: false }];

  ws.getColumn(1).width = 2;
  ws.getColumn(LABEL_COL).width = 20;
  for (let c = FIRST_DATE_COL; c <= lastDateCol; c++) {
    ws.getColumn(c).width = 10;
  }

  // Row 1: Title
  const titleRow = ws.addRow(['', 'Forecast by Section']);
  titleRow.getCell(2).font = { name: 'Calibri', size: 16, bold: true, color: { argb: primary } };
  titleRow.height = 26;
  titleRow.getCell(2).alignment = { horizontal: 'left', vertical: 'middle' };

  // Row 2: Subtitle (same as Summary)
  const subtitleRow = ws.addRow(['', subtitle]);
  subtitleRow.getCell(2).font = { name: 'Calibri', size: 10, italic: true, color: { argb: 'FF6B7785' } };
  subtitleRow.height = 18;
  subtitleRow.getCell(2).alignment = { horizontal: 'left', vertical: 'middle' };

  // Row 3: blank
  ws.addRow([]);

  // ── Helper: format date string for display (e.g. "Dec 22") ─
  const fmtDate = (dateStr) => {
    const dt = new Date(dateStr + 'T00:00:00Z');
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  };

  // ── Calendar spine (C72:Y72) — hard-coded strings ─────────
  for (let i = 0; i < periodCount; i++) {
    const cell = ws.getCell(CALENDAR_ROW, FIRST_DATE_COL + i);
    cell.value = fmtDate(allPeriods[i]);
    cell.alignment = { horizontal: 'center' };
  }

  // ── Chart data blocks ─────────────────────────────────────
  // signFlip: 1 = keep sign, -1 = flip (make positive for outflows/financing)
  function populateChartBlock(sectionId, startRow, signFlip) {
    const { topRows, groupCache } = extractTopRows(data.model, sectionId, allPeriods, 4);
    const count = Math.min(topRows.length, 4);

    for (let r = 0; r < count; r++) {
      const { row } = topRows[r];
      ws.getCell(startRow + r, LABEL_COL).value = getLabel(row);

      for (let i = 0; i < periodCount; i++) {
        const val = row.cache?.[allPeriods[i]];
        if (val != null) {
          const cell = ws.getCell(startRow + r, FIRST_DATE_COL + i);
          cell.value = (Number(val) * signFlip) / 1000;
          cell.numFmt = DATA_FMT;
        }
      }
    }

    ws.getCell(startRow + 4, LABEL_COL).value = 'All Other';
    for (let i = 0; i < periodCount; i++) {
      const dateStr = allPeriods[i];
      const total = Number(groupCache[dateStr]) || 0;
      let topSum = 0;
      for (let r = 0; r < count; r++) {
        const val = topRows[r].row.cache?.[dateStr];
        if (val != null) topSum += Number(val);
      }
      const cell = ws.getCell(startRow + 4, FIRST_DATE_COL + i);
      cell.value = ((total - topSum) * signFlip) / 1000;
      cell.numFmt = DATA_FMT;
    }
  }

  populateChartBlock('revenue', REVENUE_START, 1);
  populateChartBlock('op_inflows', INFLOWS_START, 1);
  populateChartBlock('op_outflows', OUTFLOWS_START, -1);

  // Financing total (single row, sign-flipped, in 000s)
  const financingSection = (data.model || []).find(s => s.id === 'financing');
  const financingMainGrp = financingSection?.children?.find(g => g.kind === 'group' && g.id === 'main');
  let financingCache = financingMainGrp?.cache;
  if (!financingCache) {
    const summarySection = (data.model || []).find(s => s.id === 'summary');
    const summaryFinGrp = summarySection?.children?.find(g => g.kind === 'group' && g.id === 'financing');
    financingCache = summaryFinGrp?.cache || {};
  }
  ws.getCell(FINANCING_ROW, LABEL_COL).value = 'Financing';
  for (let i = 0; i < periodCount; i++) {
    const val = financingCache[allPeriods[i]];
    if (val != null) {
      const cell = ws.getCell(FINANCING_ROW, FIRST_DATE_COL + i);
      cell.value = (Number(val) * -1) / 1000;
      cell.numFmt = DATA_FMT;
    }
  }

  // ── Collapse rows 67-95: minimal height + white-on-white so they're
  //    invisible in print/PDF but charts still read the cell values.
  for (let row = 67; row <= FINANCING_ROW; row++) {
    const r = ws.getRow(row);
    r.height = 0.75;
    for (let c = 1; c <= lastDateCol; c++) {
      const cell = r.getCell(c);
      cell.font = { ...cell.font, ...whiteFont };
      cell.fill = whiteFill;
    }
  }

  // ── Print / PDF settings — charts live above row 66 ────────
  ws.pageSetup = {
    orientation: 'landscape',
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    printArea: `B1:${colToLetter(lastDateCol)}65`,
    margins: { left: 0.3, right: 0.3, top: 0.3, bottom: 0.3, header: 0, footer: 0 },
  };

  return ws;
}

// ─────────────────────────────────────────────────────────────
// Worksheet ordering helper
// ─────────────────────────────────────────────────────────────

/**
 * Reorder workbook tabs so they appear in the given name order.
 * ExcelJS stores worksheets in a sparse array (_worksheets) indexed by sheet id.
 * The output XLSX tab order matches the iteration order of that array.
 * We rebuild it so the desired sheets come first.
 */
function reorderWorksheets(wb, desiredOrder) {
  // Grab current sheets (filter out empty slots)
  const current = wb.worksheets; // returns _worksheets.filter(Boolean)
  const byName = {};
  current.forEach(s => { byName[s.name] = s; });

  // Build ordered list of sheet objects
  const ordered = [];
  desiredOrder.forEach(name => {
    const sheet = byName[name];
    if (sheet) {
      ordered.push(sheet);
      delete byName[name];
    }
  });
  // Append any remaining sheets not in desired order
  Object.values(byName).forEach(sheet => ordered.push(sheet));

  // Rebuild the _worksheets sparse array with new id assignments.
  // ExcelJS uses 1-based ids as array indices.
  const newArr = [undefined]; // index 0 is always empty
  ordered.forEach((sheet, idx) => {
    const newId = idx + 1;
    sheet.id = newId;
    sheet.orderNo = newId;
    newArr[newId] = sheet;
  });

  wb._worksheets = newArr;
  // Update the next-id counter so any future addWorksheet works correctly
  if (wb._nextId !== undefined) {
    wb._nextId = ordered.length + 1;
  }
}

// ─────────────────────────────────────────────────────────────
// Native chart injection (JSZip post-processing)
// ─────────────────────────────────────────────────────────────

// Pre-decode template once at module load so repeated downloads don't pay
// the atob + Uint8Array cost on every call.
let _templateZipCache = null;
async function getTemplateZip() {
  if (!_templateZipCache) {
    const raw = atob(CHART_TEMPLATE_BASE64);
    const buf = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) buf[i] = raw.charCodeAt(i);
    _templateZipCache = await JSZip.loadAsync(buf);
  }
  return _templateZipCache;
}

/**
 * Inject the pre-built native Excel chart from the template into the
 * ExcelJS-generated workbook buffer.
 *
 * The template chart references Summary sheet rows 9, 10, 12 — exactly the
 * rows buildSummarySheet writes Cash Inflows, Outflows, and Ending Cash to.
 * We update the column range ($C:$Y → $C:$<lastPeriodCol>) so the chart
 * always spans the actual number of periods.
 *
 * Steps:
 *  1. Update chart1.xml data ranges for the actual period count
 *  2. Copy chart + drawing files from template into generated ZIP
 *  3. Create/update xl/worksheets/_rels/sheet2.xml.rels to reference drawing
 *  4. Inject <drawing r:id="rId1"/> into sheet2.xml (Summary)
 *  5. Patch [Content_Types].xml with chart/drawing content types
 */
async function injectChartFromTemplate(excelBuffer, periods, chartConfig = {}, branding = {}) {
  const [outputZip, templateZip] = await Promise.all([
    JSZip.loadAsync(excelBuffer),
    getTemplateZip(),
  ]);

  const nextPartNumber = (prefix, suffix) => {
    const pattern = new RegExp(`^${prefix}(\\d+)${suffix.replace('.', '\\.')}$`);
    let max = 0;
    Object.keys(outputZip.files).forEach(name => {
      const match = name.match(pattern);
      if (match) max = Math.max(max, Number(match[1]));
    });
    return max + 1;
  };

  const chartPartNum = nextPartNumber('xl/charts/chart', '.xml');
  const drawingPartNum = nextPartNumber('xl/drawings/drawing', '.xml');
  const chartXmlPath = `xl/charts/chart${chartPartNum}.xml`;
  const chartRelsPath = `xl/charts/_rels/chart${chartPartNum}.xml.rels`;
  const drawingXmlPath = `xl/drawings/drawing${drawingPartNum}.xml`;
  const drawingRelsPath = `xl/drawings/_rels/drawing${drawingPartNum}.xml.rels`;

  // ── 1. Update chart data ranges for actual period count ─────────────────
  const lastCol = colToLetter(2 + periods.length); // periods start at col C (col 3 = index 2+1)
  const inflowsRow = chartConfig.inflowsSummaryRow || 9;
  const outflowsRow = chartConfig.outflowsSummaryRow || 10;
  const endingCashRow = chartConfig.endingCashSummaryRow || 12;
  let chartXml = await templateZip.file('xl/charts/chart1.xml').async('string');
  chartXml = chartXml
    .replace(/\$B\$9/g, `$B$${inflowsRow}`)
    .replace(/\$C\$9:\$[A-Z]+\$9/g, `$C$${inflowsRow}:$${lastCol}$${inflowsRow}`)
    .replace(/\$B\$10/g, `$B$${outflowsRow}`)
    .replace(/\$C\$10:\$[A-Z]+\$10/g, `$C$${outflowsRow}:$${lastCol}$${outflowsRow}`)
    .replace(/\$B\$12/g, `$B$${endingCashRow}`)
    .replace(/\$C\$12:\$[A-Z]+\$12/g, `$C$${endingCashRow}:$${lastCol}$${endingCashRow}`);

  // ── 2. Copy chart + drawing + theme files into generated ZIP ────────────
  outputZip.file(chartXmlPath, chartXml);

  let drawingRelsXml = await templateZip.file('xl/drawings/_rels/drawing1.xml.rels').async('string');
  drawingRelsXml = drawingRelsXml.replace(/\.\.\/charts\/chart1\.xml/g, `../charts/chart${chartPartNum}.xml`);
  outputZip.file(drawingRelsPath, drawingRelsXml);

  const chartRelsTemplate = templateZip.file('xl/charts/_rels/chart1.xml.rels');
  if (chartRelsTemplate) {
    outputZip.file(chartRelsPath, await chartRelsTemplate.async('string'));
  }

  // Patch theme1.xml accent colors with org branding if provided
  let themeXml = await (templateZip.file('xl/theme/theme1.xml')?.async('string') ?? Promise.resolve(''));
  const { primary, secondary, accent } = buildBrandingColors(branding);
  if (themeXml && (branding.primaryColor || branding.secondaryColor || branding.accentColor)) {
    // accent1 → primary, accent2 → secondary, accent3 → accent
    // The theme XML stores these as <a:accent1><a:srgbClr val="RRGGBB"/></a:accent1>
    const toHex = argb => argb.slice(2); // strip leading FF
    themeXml = themeXml
      .replace(/(<a:accent1>\s*<a:srgbClr val=")[^"]+(")/,  `$1${toHex(primary)}$2`)
      .replace(/(<a:accent2>\s*<a:srgbClr val=")[^"]+(")/,  `$1${toHex(secondary)}$2`)
      .replace(/(<a:accent3>\s*<a:srgbClr val=")[^"]+(")/,  `$1${toHex(accent)}$2`);
  }

  for (const path of [
    'xl/charts/style1.xml',
    'xl/charts/colors1.xml',
  ]) {
    const f = templateZip.file(path);
    if (f) outputZip.file(path, await f.async('string'));
  }
  const drawingTemplate = templateZip.file('xl/drawings/drawing1.xml');
  if (drawingTemplate) {
    outputZip.file(drawingXmlPath, await drawingTemplate.async('string'));
  }
  // Write the (possibly patched) theme XML
  if (themeXml) outputZip.file('xl/theme/theme1.xml', themeXml);

  // ── 3. Find Summary sheet file path in generated workbook ───────────────
  let summarySheetFile = 'xl/worksheets/sheet2.xml'; // default (after reorderWorksheets)
  try {
    const wbXml = await outputZip.file('xl/workbook.xml').async('string');
    const wbRels = await outputZip.file('xl/_rels/workbook.xml.rels').async('string');
    const nameMatch = wbXml.match(/name="Summary"[^>]*r:id="([^"]+)"/);
    if (nameMatch) {
      const targetMatch = wbRels.match(new RegExp(`Id="${nameMatch[1]}"[^>]*Target="([^"]+)"`));
      if (targetMatch) {
        summarySheetFile = 'xl/' + targetMatch[1].replace(/^\.\.\//, '');
      }
    }
  } catch (_) { /* use default */ }

  const summaryRelsFile = summarySheetFile.replace(
    'xl/worksheets/',
    'xl/worksheets/_rels/'
  ).replace('.xml', '.xml.rels');

  // ── 4a. Create/update Summary sheet relationship to drawing ─────────────
  const existingRelsFile = outputZip.file(summaryRelsFile);
  let drawingRelId = 'rId1';
  if (existingRelsFile) {
    const relsXml = await existingRelsFile.async('string');
    const relIds = [...relsXml.matchAll(/Id="rId(\d+)"/g)].map(match => Number(match[1]));
    const nextRelNum = relIds.length ? Math.max(...relIds) + 1 : 1;
    drawingRelId = `rId${nextRelNum}`;
  }
  const drawingRel =
    `<Relationship Id="${drawingRelId}" ` +
    `Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/drawing" ` +
    `Target="../drawings/drawing${drawingPartNum}.xml"/>`;

  if (existingRelsFile) {
    let relsXml = await existingRelsFile.async('string');
    if (!relsXml.includes(`../drawings/drawing${drawingPartNum}.xml`)) {
      relsXml = relsXml.replace('</Relationships>', drawingRel + '</Relationships>');
    }
    outputZip.file(summaryRelsFile, relsXml);
  } else {
    outputZip.file(summaryRelsFile,
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n` +
      `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
      drawingRel +
      `</Relationships>`
    );
  }

  // ── 4b. Inject <drawing r:id="rId1"/> into Summary sheet XML ────────────
  let sheet2Xml = await outputZip.file(summarySheetFile).async('string');
  if (!sheet2Xml.includes('<drawing ')) {
    sheet2Xml = sheet2Xml.replace('</worksheet>', `<drawing r:id="${drawingRelId}"/></worksheet>`);
  }
  outputZip.file(summarySheetFile, sheet2Xml);

  // ── 5. Patch Content_Types.xml ───────────────────────────────────────────
  let ctXml = await outputZip.file('[Content_Types].xml').async('string');
  const chartContentTypes = [
    [`/${drawingXmlPath}`,         'application/vnd.openxmlformats-officedocument.drawing+xml'],
    [`/${chartXmlPath}`,           'application/vnd.openxmlformats-officedocument.drawingml.chart+xml'],
    ['/xl/charts/style1.xml',      'application/vnd.ms-office.chartstyle+xml'],
    ['/xl/charts/colors1.xml',     'application/vnd.ms-office.chartcolorstyle+xml'],
  ];
  for (const [partName, contentType] of chartContentTypes) {
    if (!ctXml.includes(partName)) {
      ctXml = ctXml.replace(
        '</Types>',
        `<Override PartName="${partName}" ContentType="${contentType}"/></Types>`
      );
    }
  }
  // ── 6. Inject Visuals charts from template ─────────────────────────────
  const templateDrawing2 = templateZip.file('xl/drawings/drawing2.xml');
  if (templateDrawing2) {
    let visualsSheetFile = null;
    try {
      const wbXml2 = await outputZip.file('xl/workbook.xml').async('string');
      const wbRels2 = await outputZip.file('xl/_rels/workbook.xml.rels').async('string');
      const vNameMatch = wbXml2.match(/name="Visuals"[^>]*r:id="([^"]+)"/);
      if (vNameMatch) {
        const vTarget = wbRels2.match(new RegExp(`Id="${vNameMatch[1]}"[^>]*Target="([^"]+)"`));
        if (vTarget) visualsSheetFile = 'xl/' + vTarget[1].replace(/^\.\.\//, '');
      }
    } catch (_) { /* Visuals sheet not found — skip */ }

    if (visualsSheetFile) {
      const vDrawingNum = nextPartNumber('xl/drawings/drawing', '.xml');
      const vDrawingPath = `xl/drawings/drawing${vDrawingNum}.xml`;
      const vDrawingRelsPath = `xl/drawings/_rels/drawing${vDrawingNum}.xml.rels`;

      const tplD2Rels = templateZip.file('xl/drawings/_rels/drawing2.xml.rels');
      let vRelsXml = tplD2Rels ? await tplD2Rels.async('string') : '';

      if (vRelsXml) {
        const chartRefs = [...vRelsXml.matchAll(/Target="\.\.\/charts\/chart(\d+)\.xml"/g)];
        for (const [, tplNum] of chartRefs) {
          const outNum = nextPartNumber('xl/charts/chart', '.xml');
          const outPath = `xl/charts/chart${outNum}.xml`;

          const tplChart = templateZip.file(`xl/charts/chart${tplNum}.xml`);
          if (tplChart) {
            let vChartXml = await tplChart.async('string');
            for (let row = 72; row <= 95; row++) {
              vChartXml = vChartXml.replace(
                new RegExp(`\\$C\\$${row}:\\$[A-Z]+\\$${row}`, 'g'),
                `$C$${row}:$${lastCol}$${row}`
              );
            }
            outputZip.file(outPath, vChartXml);

            const tplChartRels = templateZip.file(`xl/charts/_rels/chart${tplNum}.xml.rels`);
            if (tplChartRels) {
              outputZip.file(`xl/charts/_rels/chart${outNum}.xml.rels`, await tplChartRels.async('string'));
            }

            if (!ctXml.includes(`/${outPath}`)) {
              ctXml = ctXml.replace('</Types>',
                `<Override PartName="/${outPath}" ContentType="application/vnd.openxmlformats-officedocument.drawingml.chart+xml"/></Types>`);
            }
          }

          vRelsXml = vRelsXml.replace(
            `../charts/chart${tplNum}.xml`, `../charts/chart${outNum}.xml`
          );
        }
      }

      // Copy any additional style/color files from template
      for (const tplFile of Object.keys(templateZip.files)) {
        if (/^xl\/charts\/(style|colors)\d+\.xml$/.test(tplFile) && !outputZip.file(tplFile)) {
          outputZip.file(tplFile, await templateZip.file(tplFile).async('string'));
          const pn = '/' + tplFile;
          const ct = tplFile.includes('style')
            ? 'application/vnd.ms-office.chartstyle+xml'
            : 'application/vnd.ms-office.chartcolorstyle+xml';
          if (!ctXml.includes(pn)) {
            ctXml = ctXml.replace('</Types>', `<Override PartName="${pn}" ContentType="${ct}"/></Types>`);
          }
        }
      }

      outputZip.file(vDrawingPath, await templateDrawing2.async('string'));
      if (vRelsXml) outputZip.file(vDrawingRelsPath, vRelsXml);

      if (!ctXml.includes(`/${vDrawingPath}`)) {
        ctXml = ctXml.replace('</Types>',
          `<Override PartName="/${vDrawingPath}" ContentType="application/vnd.openxmlformats-officedocument.drawing+xml"/></Types>`);
      }

      // Link drawing to Visuals sheet
      const vSheetRelsFile = visualsSheetFile
        .replace('xl/worksheets/', 'xl/worksheets/_rels/')
        .replace('.xml', '.xml.rels');
      const existVRels = outputZip.file(vSheetRelsFile);
      let vRelId = 'rId1';
      if (existVRels) {
        const rx = await existVRels.async('string');
        const ids = [...rx.matchAll(/Id="rId(\d+)"/g)].map(m => Number(m[1]));
        vRelId = `rId${ids.length ? Math.max(...ids) + 1 : 1}`;
      }
      const vRelEntry =
        `<Relationship Id="${vRelId}" ` +
        `Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/drawing" ` +
        `Target="../drawings/drawing${vDrawingNum}.xml"/>`;

      if (existVRels) {
        let rx = await existVRels.async('string');
        rx = rx.replace('</Relationships>', vRelEntry + '</Relationships>');
        outputZip.file(vSheetRelsFile, rx);
      } else {
        outputZip.file(vSheetRelsFile,
          `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n` +
          `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
          vRelEntry + `</Relationships>`);
      }

      let vSheetXml = await outputZip.file(visualsSheetFile).async('string');
      if (!vSheetXml.includes('<drawing ')) {
        vSheetXml = vSheetXml.replace('</worksheet>', `<drawing r:id="${vRelId}"/></worksheet>`);
      }
      outputZip.file(visualsSheetFile, vSheetXml);
    }
  }

  outputZip.file('[Content_Types].xml', ctXml);

  // Level 1 = fastest DEFLATE with negligible size difference vs default (6)
  return outputZip.generateAsync({
    type: 'arraybuffer',
    compression: 'DEFLATE',
    compressionOptions: { level: 1 },
  });
}

// ─────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────

/**
 * Generate Excel workbook from cash JSON model
 * @param {Object} cashJson - The compiled cash JSON model
 * @param {Object} metadata - Additional metadata { workspaceName, forecastName }
 */
export async function generateExcelFromCashJson(cashJson, metadata = {}) {
  SYN_TOTAL_ROWS.clear();
  DRIVER_HEADER_ROWS.clear();
  DISPLAY_GROUP_HEADER_ROWS.clear();
  DISPLAY_GROUP_SUBTOTAL_ROWS.clear();
  
  const data = cashJson;
  const branding = metadata?.branding || {};
  const periods = extractPeriods(data);
  const actualPeriodCount = data.calendar?.actual_period_count || 0;
  const { wb, ws } = createWorkbook(branding);
  
  addHeaderRow(ws, periods, actualPeriodCount, {
    title: 'Detailed Model',
    calendar: data.calendar,
  });
  addSpacerRow(ws);
  const { tableRanges, rowById } = processModel(ws, data.model);
  populateSheet(ws, data, rowById);
  
  // Build structures for styling
  const dateCol = buildDateCol(ws);
  const labelRow = findLabelRows(ws);
  const modelIndex = indexNodes(data.model);
  
  // Apply styling (pass driver header rows + display group sets for styling)
  styleWorkbook(wb, ws, data,
    { model: data.model, byLabel: modelIndex.byLabel, byId: modelIndex.byId },
    dateCol, labelRow, rowById, tableRanges, SYN_TOTAL_ROWS, DRIVER_HEADER_ROWS,
    DISPLAY_GROUP_HEADER_ROWS, DISPLAY_GROUP_SUBTOTAL_ROWS,
    branding
  );

  // ── Build Cover sheet (insert as first tab)
  await buildCoverSheet(wb, data, metadata);

  // ── Build Summary sheet (insert as second tab, references Model)
  // Chart is injected as a native Excel chart by injectChartFromTemplate()
  // in downloadExcelFromCashJson — no PNG rendering needed here.
  const summaryResult = buildSummarySheet(wb, data, ws, rowById, metadata);
  wb._summaryChartConfig = summaryResult?.chartSourceData || null;

  // Build Visuals sheet (chart data for revenue, inflows, outflows breakdown)
  buildVisualsSheet(wb, data, metadata);

  // Reorder sheets: Cover, Summary, Visuals, Model
  reorderWorksheets(wb, ['Cover', 'Summary', 'Visuals', 'Model']);
  
  return wb;
}

async function buildExcelBlobFromCashJson(cashJson, metadata = {}) {
  const wb = await generateExcelFromCashJson(cashJson, metadata);

  // Write ExcelJS workbook to buffer, then inject the native chart from template
  const periods = extractPeriods(cashJson);
  const rawBuffer = await wb.xlsx.writeBuffer();
  const buffer = await injectChartFromTemplate(rawBuffer, periods, wb._summaryChartConfig || {}, metadata?.branding || {});

  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
}

export async function generateExcelBlobFromCashJson(cashJson, metadata = {}) {
  return buildExcelBlobFromCashJson(cashJson, metadata);
}

/**
 * Download Excel file from cash JSON model
 * @param {Object} cashJson - The compiled cash JSON model
 * @param {string} filename - Output filename (without extension)
 * @param {Object} metadata - Additional metadata { workspaceName, forecastName }
 */
export async function downloadExcelFromCashJson(cashJson, filename = 'cash_flow', metadata = {}) {
  try {
    const blob = await buildExcelBlobFromCashJson(cashJson, metadata);
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log(`✅ Downloaded ${filename}.xlsx`);
  } catch (error) {
    console.error('❌ Error generating Excel:', error);
    throw error;
  }
}
